import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Copy, RefreshCw, XCircle, Clock, CheckCircle, Phone, Search, X, Server, Globe, ShoppingCart, Calendar, Check, Zap } from 'react-feather';
import { virtualNumbersAPI } from '../api';
import { useAuth } from '../context/AuthContext';
import { formatCurrencyWithConversion } from '../utils/currency';
import PlatformIcon, { detectPlatform } from '../components/PlatformIcon';
import './VirtualNumbers.css';

// ── Helpers ──────────────────────────────────────────────────────────────────

const FLAG_MAP = {
  'argentina':'🇦🇷','australia':'🇦🇺','brazil':'🇧🇷','cameroon':'🇨🇲',
  'united states':'🇺🇸','usa':'🇺🇸','united kingdom':'🇬🇧','uk':'🇬🇧',
  'canada':'🇨🇦','france':'🇫🇷','germany':'🇩🇪','india':'🇮🇳',
  'russia':'🇷🇺','china':'🇨🇳','japan':'🇯🇵','spain':'🇪🇸',
  'italy':'🇮🇹','mexico':'🇲🇽','indonesia':'🇮🇩','turkey':'🇹🇷',
  'vietnam':'🇻🇳','thailand':'🇹🇭','philippines':'🇵🇭','egypt':'🇪🇬',
  'south africa':'🇿🇦','nigeria':'🇳🇬','colombia':'🇨🇴','poland':'🇵🇱',
  'ukraine':'🇺🇦','malaysia':'🇲🇾','kenya':'🇰🇪','ghana':'🇬🇭',
  'morocco':'🇲🇦','pakistan':'🇵🇰','bangladesh':'🇧🇩','sri lanka':'🇱🇰',
  'netherlands':'🇳🇱','switzerland':'🇨🇭','sweden':'🇸🇪','belgium':'🇧🇪',
};

function getFlag(name = '') {
  return FLAG_MAP[name.toLowerCase()] || '🌍';
}

const POPULAR_COUNTRIES = [
  { id: 1, name: 'United States', flag: '🇺🇸', code: 'US' },
  { id: 2, name: 'United Kingdom', flag: '🇬🇧', code: 'GB' },
  { id: 3, name: 'Canada', flag: '🇨🇦', code: 'CA' },
  { id: 4, name: 'France', flag: '🇫🇷', code: 'FR' },
  { id: 5, name: 'Germany', flag: '🇩🇪', code: 'DE' },
  { id: 6, name: 'Netherlands', flag: '🇳🇱', code: 'NL' },
];

const TABS = [
  { id: 'services',   label: 'Services' },
  { id: 'countries',  label: 'Countries' },
  { id: 'rent',       label: 'Rent Number' },
  { id: 'orders',     label: 'Orders' },
  { id: 'codes',      label: 'Codes' },
];

function dedupeServices(list = []) {
  const seen = new Set();
  return list.filter((service) => {
    if (service.status === 'disabled') return false;
    const key = String(service.code || service.id || '').toLowerCase().trim();
    if (!key || seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

export default function VirtualNumbers() {
  const navigate = useNavigate();
  const { user, refreshUser, publicSettings } = useAuth();

  const vnStatus = publicSettings?.virtual_numbers_status || 'beta';
  const isAdmin = user?.role === 'admin' || user?.role === 'super_admin';
  const canAccess = isAdmin || vnStatus === 'active' || ((vnStatus === 'beta' || vnStatus === 'specific_users') && (user?.is_tester === 1 || user?.is_tester === true));

  const [serverConfig, setServerConfig] = useState(null);
  const [selectedServer, setSelectedServer] = useState('smspool');
  const [tab, setTab]                 = useState('services');
  const [search, setSearch]           = useState('');

  // Data
  const [services, setServices]       = useState([]);
  const [countries, setCountries]     = useState([]);
  const [orders, setOrders]           = useState([]);
  const [prices, setPrices]           = useState([]);
  const [rentalCountries, setRentalCountries] = useState([]);
  const [rentalOrders, setRentalOrders] = useState([]);

  // Selection
  const [selectedService, setSelectedService] = useState(null);
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [selectedPrice,   setSelectedPrice]   = useState(null);
  const [selectedRentalCountry, setSelectedRentalCountry] = useState(null);
  const [selectedRentalTerm, setSelectedRentalTerm] = useState(null);

  // UI state
  const [loading, setLoading]         = useState(true);
  const [loadingPrices, setLoadingPrices] = useState(false);
  const [purchasing, setPurchasing]   = useState(false);
  const [checking,   setChecking]     = useState(null);
  const [canceling,  setCanceling]    = useState(null);
  const [cancelingRental, setCancelingRental] = useState(null);
  const [copied,     setCopied]       = useState(null);
  const [msg, setMsg]                 = useState({ text: '', type: '' });
  const [showPurchaseModal, setShowPurchaseModal] = useState(false);

  const resetPurchaseFlow = useCallback(() => {
    setSelectedService(null);
    setSelectedCountry(null);
    setSelectedPrice(null);
    setSelectedRentalCountry(null);
    setSelectedRentalTerm(null);
    setPrices([]);
    setShowPurchaseModal(false);
    setSearch('');
  }, []);

  // ── Data fetching ─────────────────────────────────────────────────────────

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [svcRes, cntRes, ordRes, rentalCntRes, rentalOrdRes, cfgRes] = await Promise.allSettled([
        virtualNumbersAPI.getServices(selectedServer),
        virtualNumbersAPI.getCountries(selectedServer),
        virtualNumbersAPI.getOrders(),
        virtualNumbersAPI.getRentalCountries(),
        virtualNumbersAPI.getRentalOrders(),
        virtualNumbersAPI.getServerConfig(),
      ]);
      if (cfgRes.status === 'fulfilled' && cfgRes.value.data) {
        setServerConfig(cfgRes.value.data);
        if (cfgRes.value.data.defaultServer && !selectedServer) {
          setSelectedServer(cfgRes.value.data.defaultServer);
        }
      }
      setServices(
        svcRes.status === 'fulfilled'
          ? dedupeServices(svcRes.value.data.services || [])
          : []
      );
      setCountries(cntRes.status === 'fulfilled' ? (cntRes.value.data.countries || []) : []);
      setOrders(ordRes.status === 'fulfilled' ? (ordRes.value.data.orders || []) : []);
      setRentalCountries(rentalCntRes.status === 'fulfilled' ? (rentalCntRes.value.data.countries || []) : []);
      setRentalOrders(rentalOrdRes.status === 'fulfilled' ? (rentalOrdRes.value.data.orders || []) : []);

      if ([svcRes, cntRes, ordRes].every((result) => result.status !== 'fulfilled')) {
        setMsg({ text: 'Failed to load data. Check your API connection.', type: 'error' });
      }
    } catch (e) {
      setMsg({ text: 'Failed to load data. Check your API connection.', type: 'error' });
    } finally {
      setLoading(false);
    }
  }, [selectedServer]);

  useEffect(() => { loadAll(); }, [loadAll]);

  // Auto-refresh orders every 10s when on Orders or Codes tab
  useEffect(() => {
    if (tab !== 'orders' && tab !== 'codes') return;
    const id = setInterval(async () => {
      try {
        const res = await virtualNumbersAPI.getOrders();
        setOrders(res.data.orders || []);
      } catch {}
    }, 10000);
    return () => clearInterval(id);
  }, [tab]);

  useEffect(() => {
    if (tab !== 'rent') return;
    const id = setInterval(async () => {
      try {
        const res = await virtualNumbersAPI.getRentalOrders();
        setRentalOrders(res.data.orders || []);
      } catch {}
    }, 10000);
    return () => clearInterval(id);
  }, [tab]);

  // ── Actions ───────────────────────────────────────────────────────────────

  const fetchPrices = async (serviceCode, serverOverride) => {
    setLoadingPrices(true);
    setPrices([]);
    try {
      const serverToUse = serverOverride !== undefined ? serverOverride : selectedServer;
      const res = await virtualNumbersAPI.getPrices(serviceCode, serverToUse);
      const newPrices = res.data.prices || [];
      setPrices(newPrices);
      setSelectedPrice((prev) => {
        if (!prev) return null;
        const updated = newPrices.find(
          (p) => String(p.countryId) === String(prev.countryId) ||
          (p.countryName && prev.countryName && p.countryName.toLowerCase() === prev.countryName.toLowerCase()) ||
          (p.countryCode && prev.countryCode && p.countryCode.toLowerCase() === prev.countryCode.toLowerCase())
        );
        return updated || null;
      });
    } catch {
      setMsg({ text: 'Failed to fetch prices for this service', type: 'error' });
    } finally {
      setLoadingPrices(false);
    }
  };

  const handleServerChange = async (server) => {
    if (server === selectedServer) return;
    setSelectedServer(server);
    if (server !== 'smspool' && tab === 'rent') {
      setTab('services');
    }
    setLoading(true);
    try {
      const [svcRes, cntRes] = await Promise.allSettled([
        virtualNumbersAPI.getServices(server),
        virtualNumbersAPI.getCountries(server),
      ]);
      setServices(
        svcRes.status === 'fulfilled'
          ? dedupeServices(svcRes.value.data.services || [])
          : []
      );
      setCountries(cntRes.status === 'fulfilled' ? (cntRes.value.data.countries || []) : []);
      if (selectedService) {
        fetchPrices(selectedService.code, server);
      }
    } catch {} finally {
      setLoading(false);
    }
  };

  const handleSelectService = (svc) => {
    setSelectedService(svc);
    setSelectedCountry(null);
    setSelectedPrice(null);
    setSearch('');
    fetchPrices(svc.code);
  };

  const handleSelectCountry = async (country) => {
    setSelectedCountry(country);
    setSelectedService(null);
    setSelectedPrice(null);
    setSearch('');
    setLoadingPrices(false);
    setPrices([]);
  };

  const handleSelectPriceRow = (priceItem) => {
    setSelectedPrice(priceItem);
    setShowPurchaseModal(true);
  };

  const handleSelectRentalCountry = (country) => {
    setSelectedRentalCountry(country);
    setSelectedRentalTerm(null);
    setSearch('');
  };

  const handlePurchase = async () => {
    if (!selectedService || !selectedPrice) return;
    setPurchasing(true);
    setMsg({ text: '', type: '' });
    try {
      await virtualNumbersAPI.purchaseNumber(
        selectedPrice.countryId,
        selectedService.code,
        selectedPrice.countryName,
        selectedService.name,
        selectedServer
      );
      setMsg({ text: '✅ Number purchased! Check Orders tab.', type: 'success' });
      setShowPurchaseModal(false);
      setSelectedPrice(null);
      setSelectedService(null);
      setSelectedCountry(null);
      setPrices([]);
      await refreshUser();
      const res = await virtualNumbersAPI.getOrders();
      setOrders(res.data.orders || []);
      setTab('orders');
    } catch (e) {
      setMsg({ text: e.response?.data?.error || 'Purchase failed', type: 'error' });
    } finally {
      setPurchasing(false);
    }
  };

  const handlePurchaseRental = async () => {
    if (!selectedRentalCountry || !selectedRentalTerm) return;
    setPurchasing(true);
    setMsg({ text: '', type: '' });
    try {
      await virtualNumbersAPI.purchaseRentalNumber(
        selectedRentalCountry.id,
        selectedRentalCountry.name,
        selectedRentalTerm
      );
      setMsg({ text: 'Rental number purchased! Check the Rent Number tab for your active rental.', type: 'success' });
      setSelectedRentalCountry(null);
      setSelectedRentalTerm(null);
      setSearch('');
      await refreshUser();
      const res = await virtualNumbersAPI.getRentalOrders();
      setRentalOrders(res.data.orders || []);
    } catch (e) {
      setMsg({ text: e.response?.data?.error || 'Rental purchase failed', type: 'error' });
    } finally {
      setPurchasing(false);
    }
  };

  const handleCheckSMS = async (orderId) => {
    setChecking(orderId);
    try {
      await virtualNumbersAPI.checkOrder(orderId);
      const res = await virtualNumbersAPI.getOrders();
      setOrders(res.data.orders || []);
    } catch {}
    setChecking(null);
  };

  const handleCancel = async (orderId) => {
    setCanceling(orderId);
    try {
      await virtualNumbersAPI.cancelOrder(orderId);
      await refreshUser();
      const res = await virtualNumbersAPI.getOrders();
      setOrders(res.data.orders || []);
      setMsg({ text: 'Order cancelled and refunded.', type: 'success' });
    } catch (e) {
      setMsg({ text: e.response?.data?.error || 'Cancel failed', type: 'error' });
    }
    setCanceling(null);
  };

  const handleCancelRental = async (rentalId) => {
    setCancelingRental(rentalId);
    try {
      await virtualNumbersAPI.cancelRentalOrder(rentalId);
      await refreshUser();
      const res = await virtualNumbersAPI.getRentalOrders();
      setRentalOrders(res.data.orders || []);
      setMsg({ text: 'Rental order cancelled and refunded.', type: 'success' });
    } catch (e) {
      setMsg({ text: e.response?.data?.error || 'Rental cancel failed', type: 'error' });
    }
    setCancelingRental(null);
  };

  const handleCopy = (text) => {
    navigator.clipboard.writeText(text).catch(() => {});
    setCopied(text);
    setTimeout(() => setCopied(null), 2000);
  };

  // ── Filtered lists ────────────────────────────────────────────────────────

  const filteredServices = useMemo(() =>
    services.filter(s => s.name.toLowerCase().includes(search.toLowerCase())),
    [services, search]);

  const filteredCountries = useMemo(() =>
    countries.filter(c => c.name.toLowerCase().includes(search.toLowerCase())),
    [countries, search]);

  const filteredRentPrices = useMemo(() =>
    prices.filter(p => p.countryName.toLowerCase().includes(search.toLowerCase())),
    [prices, search]);

  const filteredRentalCountries = useMemo(() =>
    rentalCountries.filter(c => c.name.toLowerCase().includes(search.toLowerCase()) && c.terms && c.terms.length > 0),
    [rentalCountries, search]);

  const visibleTabs = useMemo(() => {
    const list = [
      { id: 'services', label: 'Services' },
      { id: 'countries', label: 'Countries' },
    ];
    if (selectedServer === 'smspool' && rentalCountries && rentalCountries.length > 0) {
      list.push({ id: 'rent', label: 'Rent Number' });
    }
    list.push({ id: 'orders', label: 'Orders' });
    list.push({ id: 'codes', label: 'Codes' });
    return list;
  }, [selectedServer, rentalCountries]);

  const activeOrders    = orders.filter(o => o.status === 'pending');
  const completedOrders = orders.filter(o => o.status === 'completed');
  const activeRentalOrders = rentalOrders.filter(o => o.status === 'active');
  const showTopSearch =
    (tab === 'services' && !selectedService) ||
    (tab === 'countries' && !selectedCountry) ||
    (tab === 'rent' && !selectedRentalCountry);

  // ── Render ────────────────────────────────────────────────────────────────

  const renderServices = () => {
    if (loading) return <div className="vn-center"><div className="spinner" /></div>;

    if (selectedService) {
      return (
        <div>
          <button
            className="vn-back-btn"
            onClick={() => {
              setSelectedService(null);
              setSelectedPrice(null);
              setPrices([]);
              setShowPurchaseModal(false);
              setSearch('');
            }}
          >
            ‹ Back to All Services
          </button>
          <div className="vn-rent-service-header">
            <PlatformIcon platform={selectedService.name} size={48} iconSize={26} />
            <div>
              <div className="vn-rent-title">{selectedService.name}</div>
              <div className="vn-rent-sub">Select a country to continue your purchase</div>
            </div>
          </div>

          <div className="vn-search vn-rent-search">
            <Search size={16} color="var(--text-muted)" className="vn-search-icon" />
            <input
              type="text"
              placeholder="Search countries..."
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
          </div>

          {loadingPrices ? (
            <div className="vn-center"><div className="spinner" /></div>
          ) : filteredRentPrices.length === 0 ? (
            <div className="vn-empty">No countries available for this service right now.</div>
          ) : (
            <div className="vn-price-list">
              {filteredRentPrices.map((p, i) => (
                <button
                  key={`${p.countryId}-${i}`}
                  className={`vn-price-row ${selectedPrice?.countryId === p.countryId ? 'selected' : ''}`}
                  onClick={() => handleSelectPriceRow(p)}
                >
                  <div className="vn-price-left">
                    <span className="vn-price-flag">{getFlag(p.countryName)}</span>
                    <div>
                      <div className="vn-price-country">{p.countryName}</div>
                      <div className="vn-price-rate">Success Rate: {p.successRate}%</div>
                    </div>
                  </div>
                  <div className="vn-price-right">
                    <span className="vn-price-amount">{formatCurrencyWithConversion(p.price, user?.currency, 2)}</span>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      );
    }

    if (!filteredServices.length) return <div className="vn-empty">No services found.</div>;
    return (
      <div className="vn-modern-services-grid">
        {filteredServices.map(svc => {
          return (
            <div key={svc.code} className="vn-modern-service-card card">
              <div className="vn-modern-card-top">
                <PlatformIcon platform={svc.name} size={42} iconSize={22} />
                <span className="vn-code-pill">CODE: {svc.code.toUpperCase()}</span>
              </div>

              <h4 className="vn-modern-card-title">{svc.name}</h4>
              <p className="vn-modern-card-sub">Receive OTP codes • Instant Delivery</p>

              <div className="vn-modern-card-footer">
                <button
                  type="button"
                  className="btn btn-primary vn-modern-buy-btn"
                  onClick={() => handleSelectService(svc)}
                >
                  <span>Get Number</span>
                  <span className="vn-btn-arrow">›</span>
                </button>
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  const renderCountries = () => {
    if (loading) return <div className="vn-center"><div className="spinner" /></div>;

    if (selectedCountry) {
      return (
        <div>
          <button className="vn-back-btn" onClick={() => {
            setSelectedCountry(null);
            setSelectedService(null);
            setSelectedPrice(null);
            setPrices([]);
            setShowPurchaseModal(false);
            setSearch('');
          }}>
            ‹ Back to Countries
          </button>
          <div className="vn-country-header">
            <span style={{ fontSize: 32 }}>{getFlag(selectedCountry.name)}</span>
            <div>
              <div className="vn-country-name">{selectedCountry.name}</div>
              <div className="vn-country-sub">Choose a service for this country</div>
            </div>
          </div>

          <div className="vn-search vn-rent-search">
            <Search size={16} color="var(--text-muted)" className="vn-search-icon" />
            <input
              type="text"
              placeholder="Search services for this country…"
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
          </div>

          {loadingPrices && <div className="vn-center"><div className="spinner" /></div>}

          <div className="vn-list">
            {filteredServices.map(svc => {
              return (
                <button key={svc.code} className="vn-row" onClick={async () => {
                  setSelectedService(svc);
                  setLoadingPrices(true);
                  try {
                    const res = await virtualNumbersAPI.getPrices(svc.code, selectedServer);
                    const allPrices = res.data.prices || [];
                    const match = allPrices.find(p => String(p.countryId) === String(selectedCountry.id));
                    if (match) {
                      setPrices(allPrices);
                      setSelectedPrice({ ...match, countryId: selectedCountry.id, countryName: selectedCountry.name });
                      setShowPurchaseModal(true);
                    } else {
                      setMsg({ text: `${svc.name} is not available in ${selectedCountry.name}.`, type: 'error' });
                      setSelectedService(null);
                    }
                  } catch {
                    setMsg({ text: 'Failed to check service availability', type: 'error' });
                  } finally {
                    setLoadingPrices(false);
                  }
                }}>
                  <PlatformIcon platform={svc.name} size={36} iconSize={18} style={{ marginRight: 10 }} />
                  <span className="vn-row-name">{svc.name}</span>
                  <span className="vn-row-chevron">›</span>
                </button>
              );
            })}
          </div>
        </div>
      );
    }

    if (!filteredCountries.length) return <div className="vn-empty">No countries found.</div>;
    return (
      <div className="vn-list">
        {filteredCountries.map(c => (
          <button key={c.id} className="vn-row" onClick={() => handleSelectCountry(c)}>
            <div className="vn-row-icon vn-flag-icon">
              <span style={{ fontSize: 22 }}>{getFlag(c.name)}</span>
            </div>
            <div className="vn-row-info">
              <span className="vn-row-name">{c.name}</span>
              <span className="vn-row-sub">Tap to see services</span>
            </div>
            <span className="vn-row-chevron">›</span>
          </button>
        ))}
      </div>
    );
  };

  const renderRent = () => {
    if (loading) return <div className="vn-center"><div className="spinner" /></div>;

    if (selectedRentalCountry) {
      const rentalTerms = selectedRentalCountry.terms?.length ? selectedRentalCountry.terms : [];

      return (
        <div>
          <button
            className="vn-back-btn"
            onClick={() => {
              setSelectedRentalCountry(null);
              setSelectedRentalTerm(null);
              setSearch('');
            }}
          >
            ‹ Back to Rental Countries
          </button>

          <div className="vn-country-header">
            <span style={{ fontSize: 32 }}>{getFlag(selectedRentalCountry.name)}</span>
            <div>
              <div className="vn-country-name">{selectedRentalCountry.name}</div>
              <div className="vn-country-sub">Choose how long you want to keep this temporary number</div>
            </div>
          </div>

          {rentalTerms.length === 0 ? (
            <div className="vn-empty">No rental durations are available for this country right now.</div>
          ) : (
            <>
              <div className="vn-terms-grid">
                {rentalTerms.map((term) => (
                  <button
                    key={term}
                    className={`vn-term-card ${selectedRentalTerm === term ? 'selected' : ''}`}
                    onClick={() => setSelectedRentalTerm(term)}
                  >
                    <div className="vn-term-days">{term} Days</div>
                    <div className="vn-term-sub">Renewable rental term</div>
                  </button>
                ))}
              </div>

              <div className="card" style={{ marginTop: 20 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                  <span>Country</span>
                  <strong>{getFlag(selectedRentalCountry.name)} {selectedRentalCountry.name}</strong>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                  <span>Rental Duration</span>
                  <strong>{selectedRentalTerm ? `${selectedRentalTerm} days` : 'Choose a term'}</strong>
                </div>
                <p className="vn-modal-note" style={{ textAlign: 'left', marginTop: 14 }}>
                  Rental pricing comes directly from SMSPool at purchase time, then your NiceBooster markup is applied.
                </p>
                <button
                  className="btn btn-primary vn-modal-action"
                  onClick={handlePurchaseRental}
                  disabled={!selectedRentalTerm || purchasing}
                >
                  {purchasing ? 'Purchasing…' : 'Purchase Rental Number'}
                </button>
              </div>
            </>
          )}
        </div>
      );
    }

    return (
      <div className="vn-rental-layout">
        <div>
          <div className="vn-rental-header-card">
            <div>
              <div className="vn-rent-title">Long-term Rental Numbers</div>
              <div className="vn-rent-sub">Choose a supported country and keep the number for multiple days.</div>
            </div>
            <div className="vn-rental-count">{filteredRentalCountries.length}</div>
          </div>

          {filteredRentalCountries.length === 0 ? (
            <div className="vn-empty">No rental countries found.</div>
          ) : (
            <div className="vn-list">
              {filteredRentalCountries.map((country) => (
                <button key={country.id} className="vn-row" onClick={() => handleSelectRentalCountry(country)}>
                  <div className="vn-row-icon vn-flag-icon">
                    <span style={{ fontSize: 22 }}>{getFlag(country.name)}</span>
                  </div>
                  <div className="vn-row-info">
                    <span className="vn-row-name">{country.name}</span>
                    <span className="vn-row-sub">
                      {country.region || 'Unknown region'} · Terms: {(country.terms || []).join(', ') || 'N/A'} days
                    </span>
                  </div>
                  <span className="vn-row-chevron">›</span>
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="vn-rental-orders-panel">
          <div className="vn-rental-orders-title">Active Rentals</div>
          {activeRentalOrders.length === 0 ? (
            <div className="vn-empty vn-rental-orders-empty">
              <Clock size={34} color="var(--text-muted)" />
              <p>No active rental numbers yet.</p>
            </div>
          ) : (
            <div className="vn-order-list">
              {activeRentalOrders.map((order) => (
                <div key={order.id} className="vn-order-card">
                  <div className="vn-order-top">
                    <div>
                      <div className="vn-order-name">{order.country_name} · {order.rental_term_days} days</div>
                      <div className="vn-order-country">Active long-term rental</div>
                    </div>
                    <span className="badge badge-active">ACTIVE</span>
                  </div>

                  <div className="vn-order-number-box">
                    <Phone size={16} color="var(--primary)" />
                    <span className="vn-phone">+{order.phone_number}</span>
                    <button className="vn-copy-btn" onClick={() => handleCopy(order.phone_number)}>
                      {copied === order.phone_number ? <CheckCircle size={14} color="var(--green-500)" /> : <Copy size={14} />}
                    </button>
                  </div>

                  <div className="vn-order-actions">
                    <button
                      className="btn btn-sm vn-cancel-btn"
                      onClick={() => handleCancelRental(order.id)}
                      disabled={cancelingRental === order.id}
                    >
                      <XCircle size={13} /> {cancelingRental === order.id ? 'Cancelling…' : 'Cancel Rental'}
                    </button>
                  </div>

                  <div className="vn-order-cost">
                    Cost: {formatCurrencyWithConversion(order.cost, user?.currency, 2)}
                    {order.expires_at ? ` · Expires ${new Date(order.expires_at).toLocaleString()}` : ''}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderPurchaseModal = () => {
    if (!showPurchaseModal || !selectedService || !selectedPrice) return null;

    return (
      <div className="vn-modal-backdrop" onClick={() => setShowPurchaseModal(false)}>
        <div className="vn-modal-card" onClick={e => e.stopPropagation()}>
          <div className="vn-modal-head">
            <div className="vn-modal-handle" />
            <div className="vn-modal-head-row">
              <div>
                <div className="vn-modal-title">Confirm Purchase</div>
              </div>
              <button className="vn-modal-close" onClick={() => setShowPurchaseModal(false)}>
                <X size={18} />
              </button>
            </div>
          </div>

          <div className="vn-modal-summary">
            <PlatformIcon platform={selectedService.name} size={44} iconSize={24} />
            <div className="vn-modal-summary-copy">
              <strong>{selectedService.name}</strong>
              <span>Virtual Number</span>
            </div>
          </div>

          <div className="vn-modal-details">
            <div className="vn-modal-detail">
              <span>Service</span>
              <strong>{selectedService.name}</strong>
            </div>
            <div className="vn-modal-detail">
              <span>Country</span>
              <strong>{getFlag(selectedPrice.countryName)} {selectedPrice.countryName}</strong>
            </div>
            {serverConfig?.showServerSelection && (
              <div className="vn-modal-detail">
                <span>Server</span>
                <strong>{selectedServer === '5sim' ? 'Provider 2' : 'Provider 1'}</strong>
              </div>
            )}
            <div className="vn-modal-detail">
              <span>Success Rate</span>
              <strong>{selectedPrice.successRate}%</strong>
            </div>
            <div className="vn-modal-detail">
              <span>Price</span>
              <strong style={{ color: '#10B981', fontSize: 16 }}>{formatCurrencyWithConversion(selectedPrice.price, user?.currency, 2)}</strong>
            </div>
          </div>

          <p className="vn-modal-note">
            A number will be assigned to you instantly. You have a limited time to receive your SMS code.
          </p>

          <button className="btn btn-primary vn-modal-action" onClick={handlePurchase} disabled={purchasing}>
            {purchasing ? 'Purchasing…' : `Pay & Get Number (${formatCurrencyWithConversion(selectedPrice.price, user?.currency, 2)})`}
          </button>
        </div>
      </div>
    );
  };

  const renderOrders = () => {
    if (loading) return <div className="vn-center"><div className="spinner" /></div>;
    if (activeOrders.length === 0) return (
      <div className="vn-empty">
        <Clock size={40} color="var(--text-muted)" />
        <p>No active orders right now.</p>
        <button
          className="btn btn-primary btn-sm"
          style={{ marginTop: 12 }}
          onClick={() => {
            resetPurchaseFlow();
            setTab('services');
          }}
        >
          Buy a Virtual Number
        </button>
      </div>
    );
    return (
      <div className="vn-order-list">
        {activeOrders.map(order => {
          return (
            <div key={order.id} className="vn-order-card">
              <div className="vn-order-top">
                <div className="vn-order-svc">
                  <PlatformIcon platform={order.service_name} size={36} iconSize={18} />
                  <div>
                    <div className="vn-order-name">{order.service_name}</div>
                    <div className="vn-order-country">{getFlag(order.country_name)} {order.country_name}</div>
                  </div>
                </div>
                <span className="badge badge-pending">PENDING</span>
              </div>
              <div className="vn-order-number-box">
                <Phone size={16} color="var(--primary)" />
                <span className="vn-phone">+{order.phone_number}</span>
                <button className="vn-copy-btn" onClick={() => handleCopy(order.phone_number)}>
                  {copied === order.phone_number ? <CheckCircle size={14} color="var(--green-500)" /> : <Copy size={14} />}
                </button>
              </div>
              <div className="vn-order-sms-status">
                <Clock size={14} className="spin-slow" />
                <span>Waiting for SMS code…</span>
              </div>
              <div className="vn-order-actions">
                <button className="btn btn-secondary btn-sm" onClick={() => handleCheckSMS(order.order_id)} disabled={checking === order.order_id}>
                  <RefreshCw size={13} /> {checking === order.order_id ? 'Checking…' : 'Check SMS'}
                </button>
                <button className="btn btn-sm vn-cancel-btn" onClick={() => handleCancel(order.order_id)} disabled={canceling === order.order_id}>
                  <XCircle size={13} /> {canceling === order.order_id ? 'Cancelling…' : 'Cancel'}
                </button>
              </div>
              <div className="vn-order-cost">Cost: {formatCurrencyWithConversion(order.cost, user?.currency, 2)} · {new Date(order.created_at).toLocaleString()}</div>
            </div>
          );
        })}
      </div>
    );
  };

  const renderCodes = () => {
    if (loading) return <div className="vn-center"><div className="spinner" /></div>;
    if (completedOrders.length === 0) return (
      <div className="vn-empty">
        <CheckCircle size={40} color="var(--text-muted)" />
        <p>No SMS codes yet. Codes will appear here once received.</p>
      </div>
    );
    return (
      <div className="vn-order-list">
        {completedOrders.map(order => {
          return (
            <div key={order.id} className="vn-order-card vn-order-done">
              <div className="vn-order-top">
                <div className="vn-order-svc">
                  <PlatformIcon platform={order.service_name} size={36} iconSize={18} />
                  <div>
                    <div className="vn-order-name">{order.service_name}</div>
                    <div className="vn-order-country">{getFlag(order.country_name)} {order.country_name}</div>
                  </div>
                </div>
                <span className="badge badge-active">DONE</span>
              </div>
              <div className="vn-order-number-box">
                <Phone size={16} color="var(--primary)" />
                <span className="vn-phone">+{order.phone_number}</span>
              </div>
              <div className="vn-code-box">
                <div>
                  <div className="vn-code-label">SMS Code</div>
                  <div className="vn-code-value">{order.sms_code}</div>
                  {order.sms_text && <div className="vn-code-text">{order.sms_text}</div>}
                </div>
                <button className="vn-copy-btn vn-copy-large" onClick={() => handleCopy(order.sms_code)}>
                  {copied === order.sms_code ? <CheckCircle size={16} color="var(--green-500)" /> : <Copy size={16} />}
                  {copied === order.sms_code ? 'Copied!' : 'Copy'}
                </button>
              </div>
              <div className="vn-order-cost">Cost: {formatCurrencyWithConversion(order.cost, user?.currency, 2)} · {new Date(order.created_at).toLocaleString()}</div>
            </div>
          );
        })}
      </div>
    );
  };

  // ── Main render ───────────────────────────────────────────────────────────

  if (!canAccess) {
    return (
      <div className="vn-page" style={{ textAlign: 'center', padding: '60px 20px' }}>
        <div style={{ maxWidth: 480, margin: '0 auto', background: 'var(--card-bg, #ffffff)', padding: 36, borderRadius: 16, border: '1px solid var(--border-color, #e2e8f0)', boxShadow: '0 4px 12px rgba(0,0,0,0.05)' }}>
          <div style={{ width: 64, height: 64, borderRadius: 32, backgroundColor: '#fef3c7', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
            <Phone size={30} color="#d97706" />
          </div>
          <h2 style={{ fontSize: 22, fontWeight: 700, marginBottom: 10, color: 'var(--text-color, #0f172a)' }}>
            Virtual Numbers Unavailable
          </h2>
          <p style={{ color: 'var(--text-muted, #64748b)', fontSize: 14, lineHeight: 1.6, marginBottom: 24 }}>
            {vnStatus === 'disabled'
              ? 'The Virtual Number service is currently undergoing scheduled maintenance and is temporarily turned off.'
              : 'Virtual Numbers is currently restricted to authorized users. Please reach out to support if you need access.'}
          </p>
          <a href="/dashboard" className="btn btn-primary" style={{ display: 'inline-block', padding: '10px 24px', textDecoration: 'none' }}>
            Back to Dashboard
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="vn-page">
      {/* ── 1. Top Category Switcher Tabs (Matching Mobile & ServicesScreen) ── */}
      <div className="services-top-tabs" style={{ marginBottom: 18 }}>
        <button
          type="button"
          className="services-tab-btn"
          onClick={() => navigate('/dashboard/services')}
        >
          <Globe size={15} />
          <span>Social Media</span>
        </button>

        <button
          type="button"
          className="services-tab-btn active"
        >
          <Phone size={15} />
          <span>Virtual Numbers</span>
        </button>
      </div>

      {/* ── 2. Branded Promo / Hero Card ── */}
      <div className="vn-promo-hero">
        <div className="vn-promo-content">
          <h2 className="vn-promo-title">Virtual Numbers</h2>
          <p className="vn-promo-subtitle">
            Get temporary phone numbers for instant OTP verification across WhatsApp, Telegram, Google, TikTok, Facebook and 50+ countries.
          </p>
        </div>
      </div>

      {/* ── 3. Quick Action Hub Shortcuts (Matching Mobile) ── */}
      <div className="vn-quick-actions-grid">
        <button
          type="button"
          className={`vn-quick-action-card ${tab === 'services' && !selectedService ? 'active' : ''}`}
          onClick={() => {
            resetPurchaseFlow();
            setTab('services');
          }}
        >
          <div className="vn-quick-action-icon-wrap" style={{ background: '#EFF6FF', color: '#0062FF' }}>
            <ShoppingCart size={19} />
          </div>
          <span className="vn-quick-action-label">Buy Number</span>
        </button>

        <button
          type="button"
          className={`vn-quick-action-card ${tab === 'orders' ? 'active' : ''}`}
          onClick={() => {
            resetPurchaseFlow();
            setTab('orders');
          }}
        >
          <div className="vn-quick-action-icon-wrap" style={{ background: '#FFF7ED', color: '#F97316' }}>
            <Clock size={19} />
          </div>
          <div className="vn-quick-action-col">
            <span className="vn-quick-action-label">Active Orders</span>
            {activeOrders.length > 0 && (
              <span className="vn-action-badge">{activeOrders.length} active</span>
            )}
          </div>
        </button>

        {selectedServer === 'smspool' && rentalCountries.length > 0 && (
          <button
            type="button"
            className={`vn-quick-action-card ${tab === 'rent' ? 'active' : ''}`}
            onClick={() => {
              resetPurchaseFlow();
              setTab('rent');
            }}
          >
            <div className="vn-quick-action-icon-wrap" style={{ background: '#F5F3FF', color: '#8B5CF6' }}>
              <Calendar size={19} />
            </div>
            <span className="vn-quick-action-label">Rent Number</span>
          </button>
        )}

        <button
          type="button"
          className={`vn-quick-action-card ${tab === 'codes' ? 'active' : ''}`}
          onClick={() => {
            resetPurchaseFlow();
            setTab('codes');
          }}
        >
          <div className="vn-quick-action-icon-wrap" style={{ background: '#ECFDF5', color: '#10B981' }}>
            <CheckCircle size={19} />
          </div>
          <span className="vn-quick-action-label">Received Codes</span>
        </button>
      </div>

      {/* ── 4. Popular Countries Quick Selector ── */}
      <div className="vn-popular-countries-row">
        <span className="vn-popular-countries-label">Popular Countries:</span>
        <div className="vn-popular-countries-list">
          {POPULAR_COUNTRIES.map((c) => (
            <button
              key={c.code}
              type="button"
              className="vn-popular-country-chip"
              onClick={() => {
                const found = countries.find(item => item.name.toLowerCase().includes(c.name.toLowerCase()));
                if (found) {
                  handleSelectCountry(found);
                  setTab('countries');
                } else {
                  setSearch(c.name);
                  setTab('countries');
                }
              }}
            >
              <span className="country-chip-flag">{c.flag}</span>
              <span>{c.name}</span>
            </button>
          ))}
        </div>
      </div>

      {msg.text && (
        <div className={`alert alert-${msg.type}`} style={{ marginBottom: 16 }} onClick={() => setMsg({ text: '', type: '' })}>
          {msg.text}
        </div>
      )}

      {/* ── 5. Main Card Container ── */}
      <div className="card vn-card">
        {/* Search bar */}
        {showTopSearch && (
          <div className="vn-search">
            <Search size={16} color="var(--text-muted)" className="vn-search-icon" />
            <input
              type="text"
              placeholder={
                tab === 'countries'
                  ? 'Search countries…'
                  : tab === 'rent'
                    ? selectedRentalCountry ? 'Search disabled while choosing term…' : 'Search rental countries…'
                    : 'Search services (e.g. WhatsApp, Telegram, Google)…'
              }
              value={search}
              onChange={e => setSearch(e.target.value)}
              disabled={tab === 'rent' && Boolean(selectedRentalCountry)}
            />
          </div>
        )}

        {/* Server Selection (Only when both servers enabled) */}
        {serverConfig?.showServerSelection && (tab === 'services' || tab === 'countries') && (
          <div className="vn-server-selection-bar">
            <span className="server-label">
              <Server size={14} /> Provider Server:
            </span>
            <div className="server-pills">
              <button
                type="button"
                className={`server-btn ${selectedServer === 'smspool' ? 'active' : ''}`}
                onClick={() => handleServerChange('smspool')}
              >
                Provider 1
              </button>
              <button
                type="button"
                className={`server-btn ${selectedServer === '5sim' ? 'active' : ''}`}
                onClick={() => handleServerChange('5sim')}
              >
                Provider 2
              </button>
            </div>
          </div>
        )}

        {/* Tab navigation */}
        <div className="vn-tab-bar">
          {visibleTabs.map(t => {
            const isActive = tab === t.id;
            const count =
              t.id === 'orders' ? activeOrders.length :
              t.id === 'codes'  ? completedOrders.length :
              t.id === 'services' ? services.length :
              t.id === 'countries' ? countries.length :
              t.id === 'rent' ? rentalCountries.length : 0;
            return (
              <button
                key={t.id}
                className={`vn-tab-btn ${isActive ? 'active' : ''}`}
                onClick={() => {
                  setTab(t.id);
                  resetPurchaseFlow();
                }}
              >
                {t.label}
                {count > 0 && (
                  <span className={`vn-tab-badge ${t.id === 'codes' ? 'vn-tab-badge-green' : ''}`}>
                    {count}
                  </span>
                )}
              </button>
            );
          })}
        </div>

        {/* Content area */}
        <div className="vn-content">
          {tab === 'services'  && renderServices()}
          {tab === 'countries' && renderCountries()}
          {tab === 'rent'      && renderRent()}
          {tab === 'orders'    && renderOrders()}
          {tab === 'codes'     && renderCodes()}
        </div>
      </div>

      {/* Purchase Confirmation Modal */}
      {renderPurchaseModal()}
    </div>
  );
}
